import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'gatsby';
import { RecordistType } from '../types';
import { cssClampValue } from '../utils/common';

const isBrowser = typeof window !== 'undefined';
const shadowWidth = 10;

const animatedSlider = (translate: number) => keyframes`
  0% {
    transform: translateX(0);
  }

  100% {
    transform:${`translateX(-${translate + shadowWidth}px)`};
  }

  
`;

const Wrapper = styled.div<{
  $isSliding: boolean;
  $isOverflowing: boolean;
  $isAudioPlayer: boolean;
  $translate: number;
}>`
  display: flex;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  gap: 50%;
  position: relative;

  ${({ $isSliding, $isOverflowing, $translate, $isAudioPlayer }) =>
    $isOverflowing && $isSliding
      ? css`
          &::after {
            content: '';
            display: ${$isOverflowing && $isAudioPlayer ? 'block' : 'none'};
            position: absolute;
            top: 0;
            right: 0;
            width: ${`${shadowWidth}px`};
            height: 100%;
            background: linear-gradient(
              to right,
              rgba(0, 0, 0, 0) 0%,
              var(--earth-fm--color--green) 100%
            );
          }

          ul {
            animation: 5s linear ${animatedSlider($translate)}
              ${$isAudioPlayer ? 'alternate 2' : 'forwards'};
            animation-play-state: running;
          }
          // Pause the animation when hovering over the recordists list
          &:hover {
            ul {
              animation-play-state: paused;
            }
          }
        `
      : null}
`;

const Recordists = styled.ul`
  display: flex;
  width: max-content;
  padding: 0;
  margin: ${cssClampValue(4, 8)} 0px;
  list-style: none;
  line-height: 1.27;
  gap: 5px;
  animation: none;

  li:focus-within {
    outline: auto;
    outline-color: var(--earth-fm--color--blue);
  }

  li:not(:last-child) {
    &::after {
      content: ', ';
    }
  }

  a:focus {
    outline: 0;
  }
`;

type Props = {
  recordists: RecordistType[] | null;
  recordist?: string; // Legacy field
  recordistUri?: string | null; // Legacy field
  isSliding?: boolean;
  isAudioPlayer?: boolean;
  className?: string;
};

export default function AnimatedRecordistsList({
  recordists,
  isSliding = false,
  isAudioPlayer = false,
  recordist = '',
  recordistUri = null,
  className = '',
}: Props) {
  const [isRecordistsOverflowing, setIsRecordistsOverflowing] = React.useState(false);
  const [translate, setTranslate] = React.useState(0);
  const [recordistsList, setRecordistsList] = React.useState<RecordistType[] | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const checkOverflow = React.useCallback(() => {
    const { current } = wrapperRef;

    if (current) {
      setIsRecordistsOverflowing(current.scrollWidth > current.clientWidth);
      setTranslate(current.scrollWidth - current.clientWidth);
    }
  }, []);

  React.useEffect(() => {
    if (!isBrowser) {
      return () => {};
    }

    checkOverflow();

    if (isSliding) {
      checkOverflow();
    }

    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [checkOverflow, isRecordistsOverflowing, isSliding]);

  React.useEffect(() => {
    if (recordists) {
      setRecordistsList(recordists);
    } else if (recordist) {
      setRecordistsList([{ id: 0, title: recordist, uri: recordistUri }]);
    }
  }, [recordists, recordist, recordistUri]);

  return (
    <Wrapper
      ref={wrapperRef}
      $isSliding={isSliding}
      $isOverflowing={isRecordistsOverflowing}
      $isAudioPlayer={isAudioPlayer}
      $translate={translate}
      className={className}
    >
      <Recordists className={`${className}__recordists`}>
        {recordistsList &&
          recordistsList.map((recordistListItem) => (
            <li key={recordistListItem.id}>
              <Link to={recordistListItem.uri || ''}>{recordistListItem.title}</Link>
            </li>
          ))}
      </Recordists>
    </Wrapper>
  );
}
